import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, ClipboardList, Loader2, Plus, Trash2, Link2, Copy, CheckCircle2 } from 'lucide-react';
import api from '@/utils/api';

export default function AdminQuestions({ user, onLogout }) {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  
  const [newQuestion, setNewQuestion] = useState({
    order: 0,
    prompt: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    map_a: 'D',
    map_b: 'I',
    map_c: 'S',
    map_d: 'C'
  });

  useEffect(() => {
    fetchQuestions();
    fetchEvaluations();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await api.get('/questions');
      setQuestions(response.data);
      // Set next order number
      if (response.data.length > 0) {
        const maxOrder = Math.max(...response.data.map(q => q.order));
        setNewQuestion(prev => ({ ...prev, order: maxOrder + 1 }));
      } else {
        setNewQuestion(prev => ({ ...prev, order: 1 }));
      }
    } catch (error) {
      toast.error('Erro ao carregar perguntas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluations = async () => {
    try {
      const response = await api.get('/evaluations');
      setEvaluations(response.data);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    try {
      await api.post('/questions', newQuestion);
      toast.success('Pergunta adicionada com sucesso!');
      setShowAddDialog(false);
      setNewQuestion({
        order: newQuestion.order + 1,
        prompt: '',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        map_a: 'D',
        map_b: 'I',
        map_c: 'S',
        map_d: 'C'
      });
      fetchQuestions();
    } catch (error) {
      toast.error('Erro ao adicionar pergunta');
      console.error(error);
    }
  };

  const handleDeleteQuestion = async (questionId, questionText) => {
    if (!window.confirm(`Tem certeza que deseja desativar esta pergunta?\n\n"${questionText}"`)) {
      return;
    }

    try {
      await api.delete(`/questions/${questionId}`);
      toast.success('Pergunta desativada');
      fetchQuestions();
    } catch (error) {
      toast.error('Erro ao desativar pergunta');
      console.error(error);
    }
  };

  const copyToClipboard = (publicLink, evalId) => {
    const fullUrl = `${window.location.origin}/public/register/${publicLink}`;
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(fullUrl)
        .then(() => {
          toast.success('Link copiado!');
          setCopiedLink(evalId);
          setTimeout(() => setCopiedLink(null), 2000);
        })
        .catch(() => {
          fallbackCopyToClipboard(fullUrl, evalId);
        });
    } else {
      fallbackCopyToClipboard(fullUrl, evalId);
    }
  };

  const fallbackCopyToClipboard = (text, evalId) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      toast.success('Link copiado!');
      setCopiedLink(evalId);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      toast.error('Erro ao copiar. Por favor, copie manualmente.');
    }
    
    document.body.removeChild(textArea);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      {/* Header */}
      <header className="glass border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Dashboard
            </Button>
            <div className="text-right">
              <p className="text-sm text-gray-600">{user?.name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Evaluations Section */}
        {evaluations.length > 0 && (
          <Card className="shadow-premium border-0 glass mb-6">
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                <Link2 className="w-5 h-5 mr-2 text-indigo-600" />
                Links Públicos de Avaliação
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">Compartilhe estes links para que candidatos possam se registrar e fazer a avaliação</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {evaluations.map((evaluation) => (
                  <div key={evaluation.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">{evaluation.name}</span>
                      {evaluation.description && (
                        <span className="text-xs text-gray-500">{evaluation.description}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={`${window.location.origin}/public/register/${evaluation.public_link}`}
                        readOnly
                        className="font-mono text-sm bg-white"
                      />
                      <Button
                        onClick={() => copyToClipboard(evaluation.public_link, evaluation.id)}
                        className={`${
                          copiedLink === evaluation.id
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-indigo-600 hover:bg-indigo-700'
                        }`}
                      >
                        {copiedLink === evaluation.id ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Copiado!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copiar
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="shadow-premium border-0 glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl flex items-center" style={{fontFamily: 'Manrope'}}>
                  <ClipboardList className="w-6 h-6 mr-3 text-indigo-600" />
                  Gerenciar Perguntas DISC
                </CardTitle>
                <p className="text-gray-600 mt-2">Banco de perguntas da avaliação comportamental</p>
              </div>
              <div className="flex items-center gap-3">
                <Badge className="text-base px-4 py-2">{questions.length} perguntas</Badge>
                
                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Nova Pergunta
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Adicionar Nova Pergunta DISC</DialogTitle>
                      <DialogDescription>
                        Crie uma nova pergunta para o teste comportamental
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddQuestion} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="order">Ordem *</Label>
                        <Input
                          id="order"
                          type="number"
                          value={newQuestion.order}
                          onChange={(e) => setNewQuestion({...newQuestion, order: parseInt(e.target.value)})}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="prompt">Pergunta *</Label>
                        <Textarea
                          id="prompt"
                          value={newQuestion.prompt}
                          onChange={(e) => setNewQuestion({...newQuestion, prompt: e.target.value})}
                          required
                          rows={3}
                          placeholder="Ex: Quando enfrento um problema inesperado, eu costumo:"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="option_a">Opção A *</Label>
                          <Input
                            id="option_a"
                            value={newQuestion.option_a}
                            onChange={(e) => setNewQuestion({...newQuestion, option_a: e.target.value})}
                            required
                            placeholder="Texto da alternativa A"
                          />
                          <Select value={newQuestion.map_a} onValueChange={(v) => setNewQuestion({...newQuestion, map_a: v})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Fator DISC" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="D">D - Dominância</SelectItem>
                              <SelectItem value="I">I - Influência</SelectItem>
                              <SelectItem value="S">S - Estabilidade</SelectItem>
                              <SelectItem value="C">C - Conformidade</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="option_b">Opção B *</Label>
                          <Input
                            id="option_b"
                            value={newQuestion.option_b}
                            onChange={(e) => setNewQuestion({...newQuestion, option_b: e.target.value})}
                            required
                            placeholder="Texto da alternativa B"
                          />
                          <Select value={newQuestion.map_b} onValueChange={(v) => setNewQuestion({...newQuestion, map_b: v})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Fator DISC" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="D">D - Dominância</SelectItem>
                              <SelectItem value="I">I - Influência</SelectItem>
                              <SelectItem value="S">S - Estabilidade</SelectItem>
                              <SelectItem value="C">C - Conformidade</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="option_c">Opção C *</Label>
                          <Input
                            id="option_c"
                            value={newQuestion.option_c}
                            onChange={(e) => setNewQuestion({...newQuestion, option_c: e.target.value})}
                            required
                            placeholder="Texto da alternativa C"
                          />
                          <Select value={newQuestion.map_c} onValueChange={(v) => setNewQuestion({...newQuestion, map_c: v})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Fator DISC" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="D">D - Dominância</SelectItem>
                              <SelectItem value="I">I - Influência</SelectItem>
                              <SelectItem value="S">S - Estabilidade</SelectItem>
                              <SelectItem value="C">C - Conformidade</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="option_d">Opção D *</Label>
                          <Input
                            id="option_d"
                            value={newQuestion.option_d}
                            onChange={(e) => setNewQuestion({...newQuestion, option_d: e.target.value})}
                            required
                            placeholder="Texto da alternativa D"
                          />
                          <Select value={newQuestion.map_d} onValueChange={(v) => setNewQuestion({...newQuestion, map_d: v})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Fator DISC" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="D">D - Dominância</SelectItem>
                              <SelectItem value="I">I - Influência</SelectItem>
                              <SelectItem value="S">S - Estabilidade</SelectItem>
                              <SelectItem value="C">C - Conformidade</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <strong>💡 Dica:</strong> Mapeie cada alternativa para o fator DISC correspondente. 
                          Geralmente: A=D (Dominância), B=I (Influência), C=S (Estabilidade), D=C (Conformidade)
                        </p>
                      </div>

                      <Button type="submit" className="w-full">
                        Adicionar Pergunta
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
                <p className="text-gray-500">Carregando perguntas...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {questions.map((question, index) => (
                  <div key={question.id} className="border rounded-lg p-6 bg-white hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start flex-1">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-4 flex-shrink-0">
                          <span className="font-bold text-indigo-600">{question.order}</span>
                        </div>
                        <p className="text-lg font-semibold text-gray-900 flex-1">{question.prompt}</p>
                      </div>
                      {user?.role === 'ADMIN' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteQuestion(question.id, question.prompt)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-4"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="ml-14 space-y-3">
                      <div className="flex items-start">
                        <Badge variant="outline" className="mr-3 mt-0.5">A → {question.map_a}</Badge>
                        <p className="text-gray-700 flex-1">{question.option_a}</p>
                      </div>
                      <div className="flex items-start">
                        <Badge variant="outline" className="mr-3 mt-0.5">B → {question.map_b}</Badge>
                        <p className="text-gray-700 flex-1">{question.option_b}</p>
                      </div>
                      <div className="flex items-start">
                        <Badge variant="outline" className="mr-3 mt-0.5">C → {question.map_c}</Badge>
                        <p className="text-gray-700 flex-1">{question.option_c}</p>
                      </div>
                      <div className="flex items-start">
                        <Badge variant="outline" className="mr-3 mt-0.5">D → {question.map_d}</Badge>
                        <p className="text-gray-700 flex-1">{question.option_d}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
